import {
  Box,
  Button,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { FilePlus, Search } from 'tabler-icons-react';
import {
  Th,
  EquipmentDetailModal,
  EquipmentEditModal,
  EquipmentCreateModal,
  InfiniteScrollArea,
} from '../../components';
import { useAllEquipment } from '../../hooks';
import { IEquipment } from '../../models';

function filterData(data: IEquipment[], search: string, visibleKeys: string[]) {
  const keys = visibleKeys;
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    keys.some((key) => (item[key] ?? '-').toString().toLowerCase().includes(query))
  );
}

function sortData(
  data: IEquipment[],
  payload: { sortBy: keyof IEquipment; reversed: boolean; search: string },
  visibleKeys: string[]
) {
  if (!payload.sortBy) {
    return filterData(data, payload.search, visibleKeys);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return (b[payload.sortBy] ?? '000ForceToBottom')
          .toString()
          .localeCompare((a[payload.sortBy] ?? '000ForceToBottom').toString());
      }

      return (a[payload.sortBy] ?? 'zzzForceToBottom')
        .toString()
        .localeCompare((b[payload.sortBy] ?? 'zzzForceToBottom').toString());
    }),
    payload.search,
    visibleKeys
  );
}

type Props = {};

const EquipmentSubPage = (props: Props) => {
  const theme = useMantineTheme();

  const [equipmentCreated, setEquipmentCreated] = useState<boolean>(false);
  const [equipmentUpdated, setEquipmentUpdated] = useState<boolean>(false);
  const [equipmentUpdateError, setEquipmentUpdateError] = useState<boolean>(false);
  const [equipmentDeleted, setEquipmentDeleted] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [detailEquipment, setDetailEquipment] = useState<IEquipment>({
    equipmentId: '',
    equipmentTypeId: '',
    equipmentTypeName: '',
    name: '',
    notes: '',
    hasSerialNumber: false,
    lastUpdatedUserId: '',
    lastUpdatedDateTime: new Date(),
  });

  const defaultKeys: string[] = ['name', 'equipmentTypeName'];

  let equipment = useAllEquipment();

  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(equipment.data);
  const [sortBy, setSortBy] = useState<keyof IEquipment>(null!);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const setSorting = (field: keyof IEquipment) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(equipment.data!, { sortBy: field, reversed, search }, defaultKeys));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(
        equipment.data!,
        { sortBy, reversed: reverseSortDirection, search: value },
        defaultKeys
      )
    );
  };

  const rows = sortedData?.map((row) => (
    <tr
      key={row.equipmentId}
      style={{ cursor: 'pointer' }}
      onClick={() => {
        setDetailEquipment(row);
        setDetailModalOpen(true);
      }}
    >
      <td style={{ width: '33%', wordWrap: 'break-word' }}>{`${row.name}`}</td>
      <td style={{ width: '33%', wordWrap: 'break-word' }}>{`${row.equipmentTypeName}`}</td>
    </tr>
  ));

  useEffect(() => {
    if (!equipment.data) {
      setSortedData(equipment.data);
    } else {
      setSortedData(
        sortData(equipment.data, { sortBy, reversed: reverseSortDirection, search }, defaultKeys)
      );
    }
  }, [equipment.data, reverseSortDirection, search, sortBy]);

  return (
    <Box style={{ height: '100%' }}>
      <Box style={{ height: '100%' }}>
        <TextInput
          placeholder="Search by any field"
          mb="md"
          icon={<Search size={14} />}
          value={search}
          onChange={handleSearchChange}
        />
        <Divider />
        <ScrollArea type="hover" style={{ width: '100%' }}>
          <Table style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <Th
                  sorted={sortBy === 'name'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('name')}
                  style={{ width: '33%' }}
                >
                  Equipment Name
                </Th>
                <Th
                  sorted={sortBy === 'equipmentTypeName'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('equipmentTypeName')}
                  style={{ width: '33%' }}
                >
                  Category
                </Th>
              </tr>
            </thead>
          </Table>
          {equipment.data && <InfiniteScrollArea rows={rows} height={350} />}
          {!equipment.data && equipment.isLoading && (
            <Stack style={{ height: '350px' }} align="center" justify="center">
              <Loader size={84} />
              <Text size="lg" weight={500}>
                Loading...
              </Text>
            </Stack>
          )}
        </ScrollArea>
        <Divider />
        <Group noWrap grow position="apart" mt="lg">
          <Button
            variant="outline"
            mb="lg"
            onClick={() => {
              setCreateModalOpen(true);
            }}
          >
            <FilePlus style={{ marginRight: 5 }} />
            Add New Equipment
          </Button>
        </Group>
      </Box>

      <EquipmentCreateModal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
        }}
        onCreate={(isCreated, newEquipment) => {
          setEquipmentCreated(isCreated);
          setDetailEquipment(newEquipment);
          setCreateModalOpen(false);
          setDetailModalOpen(true);
        }}
      />
      <EquipmentDetailModal
        equipment={detailEquipment}
        opened={detailModalOpen}
        isCreated={equipmentCreated}
        isUpdated={equipmentUpdated}
        isDeleted={equipmentDeleted}
        isError={equipmentUpdateError}
        onClose={() => {
          setEquipmentUpdated(false);
          setEquipmentUpdateError(false);
          setEquipmentCreated(false);
          setEquipmentDeleted(false);
          setDetailModalOpen(false);
        }}
        onEdit={() => {
          setEditModalOpen(true);
          setDetailModalOpen(false);
        }}
      />
      <EquipmentEditModal
        equipment={detailEquipment}
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setDetailModalOpen(true);
        }}
        onEdited={(isUpdated, isError, updatedEquipment) => {
          setEditModalOpen(false);
          setDetailModalOpen(true);
          setEquipmentUpdated(isUpdated);
          setEquipmentUpdateError(isError);
          setDetailEquipment(updatedEquipment);
        }}
        onDeleted={() => {
          setEditModalOpen(false);
          setDetailModalOpen(true);
          setEquipmentDeleted(true);
        }}
      />
    </Box>
  );
};

export default EquipmentSubPage;
